import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Seo from "domain/SeoAndAnalytics/Seo";
import { Category } from "domain/BlogAndNews/Category";
import Breadcrumb from "components/Accessories/Breadcrumb";
import VinWidget from "components/VinSearch/VinWidget";
import { Breadcrumbs } from "utils/breadcrumbs";
import { useRoutes } from "domain/Routing/hooks/useRoutes";
import { Container, Variant } from "@carfax/web-ui/elements/Container";
import { TCategory } from "@carfax/contentful/types/wrappers";
import { styles } from "styles/pageCommonStyles.style";
import { ModuleStickyVinSearch } from "@carfax/contentful/PageModularModule";
import { useLanguage } from "hooks/useLanguage";
import { resolveCategoryCanonical } from "domain/BlogAndNews/utils/resolveCategoryCanonical";
import type { BlogPostsResult, LocaleSlugMap } from "@carfax/contentful/blog-content-api";
import { buildHrefLangAlternatesForLocaleSlugs, blogPostPath } from "domain/SeoAndAnalytics/hrefLangAlternates";

const StickyVinSearch = dynamic(() => import("domain/CommonModules/StickyVinSearch/StickyVinSearch"));

export interface CategoryPageProps {
  category: TCategory;
  blogPosts: BlogPostsResult;
  stickyVinSearch?: ModuleStickyVinSearch;
  translatedSlugs?: LocaleSlugMap | null;
}

export const CategoryPage = ({ category, blogPosts, stickyVinSearch, translatedSlugs }: CategoryPageProps) => {
  const t = useTranslations("common");
  const routes = useRoutes();
  const router = useRouter();
  const currentLanguage = useLanguage();

  const hrefLangAlternates = buildHrefLangAlternatesForLocaleSlugs({
    localeSlugs: translatedSlugs,
    toPath: blogPostPath,
    currentLanguage,
  });

  const canonical = resolveCategoryCanonical(category.fields.canonicalLink, router.asPath.split("?")[0]);

  return (
    <>
      <Seo
        title={category.fields.metaTitle}
        image={category.fields.featuredImage?.fields?.file?.url}
        description={category.fields.metaDescription}
        canonical={canonical}
        hrefLangAlternates={hrefLangAlternates}
      />
      <Container paddingTop={Variant.Top.Standard}>
        <div className={styles.breadcrumbWrapper()}>
          <Breadcrumb
            items={[
              Breadcrumbs.home(t),
              Breadcrumbs.blog(t, routes),
              Breadcrumbs.category(category.fields.title, category.fields.slug),
            ]}
          />
        </div>
        <Category
          categoryIconSrc={category.fields.categoryIcon?.fields?.file?.url}
          categoryIconAlt={category.fields.categoryIcon?.fields?.file?.fileName}
          categoryIconTitle={category.fields.categoryIcon?.fields?.title}
          categoryTitle={category.fields.title}
          categoryText={category.fields.categoryText}
          categoryFeatured={category.fields.featuredImage?.fields?.file}
          blogPosts={blogPosts.entries}
        />
      </Container>
      <VinWidget />
      {stickyVinSearch && <StickyVinSearch module={stickyVinSearch} />}
    </>
  );
};
